import { createSelector } from 'reselect';
import { createReducer } from 'typesafe-actions';
import { RootAction } from 'actions';
import { IProductionHoursApiState, Store } from 'store/storeTypes';
import { systemGroupSelector } from 'reducers/ui/systemGroupUiReducer';
import { SYSTEM_GROUP_TYPE_VALUES } from 'components/WorkingHours/utils/productionHoursEnumMaps';
import {
  getProductionHoursConfigurationAC,
  saveProductionHoursConfigurationAC,
  getProductionHoursMatrixAC,
  getProductionHoursCalendarAC,
  getProductionHoursHolidaysAC,
  saveProductionHoursHolidayAC,
  deleteProductionHoursHolidayAC,
} from 'actions/productionHoursApiActions';

const INIT_STATE: IProductionHoursApiState = {
  configuration: null,
  isLoadingConfiguration: false,
  isConfigurationError: false,

  isSavingConfiguration: false,
  isSaveConfigurationError: false,

  matrix: null,
  isLoadingMatrix: false,
  isMatrixError: false,

  calendar: null,
  isLoadingCalendar: false,
  isCalendarError: false,

  holidays: null,
  isLoadingHolidays: false,
  isHolidaysError: false,

  isSavingHoliday: false,
  isSaveHolidayError: false,

  isDeletingHoliday: false,
  isDeleteHolidayError: false,
};

const reducer = createReducer<IProductionHoursApiState, RootAction>(INIT_STATE)
  .handleAction(getProductionHoursConfigurationAC.request, state => ({
    ...state,
    isLoadingConfiguration: true,
    isConfigurationError: false,
  }))
  .handleAction(getProductionHoursConfigurationAC.success, (state, action) => ({
    ...state,
    configuration: action.payload,
    isLoadingConfiguration: false,
  }))
  .handleAction(getProductionHoursConfigurationAC.failure, state => ({
    ...state,
    isLoadingConfiguration: false,
    isConfigurationError: true,
  }))

  .handleAction(saveProductionHoursConfigurationAC.request, state => ({
    ...state,
    isSavingConfiguration: true,
    isSaveConfigurationError: false,
  }))
  .handleAction(saveProductionHoursConfigurationAC.success, state => ({
    ...state,
    isSavingConfiguration: false,
  }))
  .handleAction(saveProductionHoursConfigurationAC.failure, state => ({
    ...state,
    isSavingConfiguration: false,
    isSaveConfigurationError: true,
  }))

  .handleAction(getProductionHoursMatrixAC.request, state => ({
    ...state,
    isLoadingMatrix: true,
    isMatrixError: false,
  }))
  .handleAction(getProductionHoursMatrixAC.success, (state, action) => ({
    ...state,
    matrix: action.payload,
    isLoadingMatrix: false,
  }))
  .handleAction(getProductionHoursMatrixAC.failure, state => ({
    ...state,
    isLoadingMatrix: false,
    isMatrixError: true,
  }))

  .handleAction(getProductionHoursCalendarAC.request, state => ({
    ...state,
    isLoadingCalendar: true,
    isCalendarError: false,
  }))
  .handleAction(getProductionHoursCalendarAC.success, (state, action) => ({
    ...state,
    calendar: action.payload,
    isLoadingCalendar: false,
  }))
  .handleAction(getProductionHoursCalendarAC.failure, state => ({
    ...state,
    isLoadingCalendar: false,
    isCalendarError: true,
  }))

  .handleAction(getProductionHoursHolidaysAC.request, state => ({
    ...state,
    isLoadingHolidays: true,
    isHolidaysError: false,
  }))
  .handleAction(getProductionHoursHolidaysAC.success, (state, action) => ({
    ...state,
    holidays: action.payload,
    isLoadingHolidays: false,
  }))
  .handleAction(getProductionHoursHolidaysAC.failure, state => ({
    ...state,
    isLoadingHolidays: false,
    isHolidaysError: true,
  }))

  .handleAction(saveProductionHoursHolidayAC.request, state => ({
    ...state,
    isSavingHoliday: true,
    isSaveHolidayError: false,
  }))
  .handleAction(saveProductionHoursHolidayAC.success, state => ({
    ...state,
    isSavingHoliday: false,
  }))
  .handleAction(saveProductionHoursHolidayAC.failure, state => ({
    ...state,
    isSavingHoliday: false,
    isSaveHolidayError: true,
  }))

  .handleAction(deleteProductionHoursHolidayAC.request, state => ({
    ...state,
    isDeletingHoliday: true,
    isDeleteHolidayError: false,
  }))
  .handleAction(deleteProductionHoursHolidayAC.success, state => ({
    ...state,
    isDeletingHoliday: false,
  }))
  .handleAction(deleteProductionHoursHolidayAC.failure, state => ({
    ...state,
    isDeletingHoliday: false,
    isDeleteHolidayError: true,
  }));

export default reducer;

//*selectors

export const productionHoursApiSelector = (state: Store) => state.productionHoursApi;
export const productionHoursMatrixSelector = createSelector(
  [productionHoursApiSelector],
  productionHoursApi => productionHoursApi.matrix
);
// True if at least one site/system-group has production hours configured for the tenant.
export const isAnyProductionHoursSetSelector = createSelector(
  [productionHoursMatrixSelector],
  matrix => !!matrix?.entries.some(entry => entry.isSet)
);

// Names of the sites that DO have production hours configured for the currently selected
// system group (DTG/DTF/Apollo) - i.e. the matrix's isSet: true entries for that type.
// Used to grey out sites in the machine filter that fall back to 24h when Production mode
// is on, since their schedule wouldn't actually be limited to any configured window.
export const sitesWithProductionHoursSetSelector = createSelector(
  [productionHoursMatrixSelector, systemGroupSelector],
  (matrix, selectedSystemGroup): Set<string> => {
    const typeValue = SYSTEM_GROUP_TYPE_VALUES[selectedSystemGroup];
    if (!matrix || typeValue === undefined) return new Set();
    const result = new Set<string>();
    matrix.entries.forEach(entry => {
      if (entry.systemGroupType === typeValue && entry.isSet) result.add(entry.siteName);
    });
    return result;
  }
);
